import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ReviewScene, ScanReview } from '../types';
import {
  approveScene,
  fetchScanReview,
  promptScene,
  publishScan,
  redoScene,
  regenerateScene,
  undoScene,
} from '../api';
import { ConfirmDialog } from '../components/HistoricalTable';
import { PromptDialog } from '../components/PromptDialog';
import { createLogger } from '../../utils/logger';

const log = createLogger('InteriorReviewScanPage');

/**
 * Cache-busting display URL for a crop.
 *
 * Presigned S3 URLs (SigV4) sign their entire query string, so appending
 * an extra parameter would invalidate the signature (403). Each action
 * response mints a fresh presigned URL (new signature/expiry), so those
 * are already unique and we leave them untouched. For local / unsigned
 * URLs we append the per-scene version timestamp so the browser refetches
 * the newly written bytes instead of serving a stale cache hit (His
 * Majesty's explicit per-image refresh requirement).
 */
function displaySrc(url: string, version: number): string {
  if (version === 0) return url;
  if (/[?&]X-Amz-Signature=/i.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${version}`;
}

interface SceneCardProps {
  scene: ReviewScene;
  version: number;
  busy: boolean;
  error: string | null;
  onApprove: (scene: string) => void;
  onRegenerate: (scene: string) => void;
  onPrompt: (scene: string) => void;
  onUndo: (scene: string) => void;
  onRedo: (scene: string) => void;
  /** Open the fullscreen raw-vs-current overlay for this scene. */
  onOpenFullscreen: (scene: string) => void;
}

function ActionButton({
  onClick,
  disabled,
  title,
  active,
  variant = 'neutral',
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  title: string;
  active?: boolean;
  variant?: 'neutral' | 'approve';
  children: React.ReactNode;
}) {
  const base =
    'p-1.5 rounded-md transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed';
  const tone =
    variant === 'approve'
      ? active
        ? 'text-white bg-[#70B77E] hover:bg-[#70B77E]/90'
        : 'text-[#70B77E] bg-[#70B77E]/10 hover:bg-[#70B77E]/20'
      : 'text-slate-600 bg-slate-50 hover:bg-slate-100';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`${base} ${tone}`}
    >
      {children}
    </button>
  );
}

interface ActionBarProps {
  scene: ReviewScene;
  busy: boolean;
  onApprove: (scene: string) => void;
  onRegenerate: (scene: string) => void;
  onPrompt: (scene: string) => void;
  onUndo: (scene: string) => void;
  onRedo: (scene: string) => void;
}

/**
 * The Approve / Regenerate / Prompt / Undo / Redo bar. Shared verbatim
 * between the grid card and the fullscreen overlay so both surfaces wire
 * to the SAME handlers and the SAME per-scene busy state — an action
 * fired from either disables every copy of these buttons for that scene.
 */
function ActionBar({
  scene,
  busy,
  onApprove,
  onRegenerate,
  onPrompt,
  onUndo,
  onRedo,
}: ActionBarProps) {
  const approved = scene.status === 'approved';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <ActionButton
        onClick={() => onApprove(scene.scene)}
        disabled={busy}
        active={approved}
        variant="approve"
        title={approved ? 'Approved (click to unapprove)' : 'Approve'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
        </svg>
      </ActionButton>

      <ActionButton
        onClick={() => onRegenerate(scene.scene)}
        disabled={busy || !scene.hasPayload}
        title={scene.hasPayload ? 'Regenerate' : 'Regenerate unavailable (no generation payload)'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </ActionButton>

      <ActionButton
        onClick={() => onPrompt(scene.scene)}
        disabled={busy}
        title="Prompt"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </ActionButton>

      <span className="mx-0.5 w-px h-5 bg-slate-200" aria-hidden="true" />

      <ActionButton
        onClick={() => onUndo(scene.scene)}
        disabled={busy || !scene.canUndo}
        title="Undo"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
        </svg>
      </ActionButton>

      <ActionButton
        onClick={() => onRedo(scene.scene)}
        disabled={busy || !scene.canRedo}
        title="Redo"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2m15-7l-4-4m4 4l-4 4" />
        </svg>
      </ActionButton>
    </div>
  );
}

/** Compact status pill ("In progress" gray / "Approved" green). Shared
 * by the card and the fullscreen Current panel. */
function StatusBadge({ approved }: { approved: boolean }) {
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${
        approved ? 'status-completed' : 'bg-slate-200 text-slate-600'
      }`}
    >
      {approved ? (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      )}
      {approved ? 'Approved' : 'In progress'}
    </span>
  );
}

/** Inline spinner shown over a busy "Current" image. */
function BusyOverlay() {
  return (
    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
      <svg className="w-8 h-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  );
}

function SceneCard({
  scene,
  version,
  busy,
  error,
  onApprove,
  onRegenerate,
  onPrompt,
  onUndo,
  onRedo,
  onOpenFullscreen,
}: SceneCardProps) {
  const [imgErrored, setImgErrored] = useState(false);
  const approved = scene.status === 'approved';

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-slate-200 shadow-soft flex flex-col">
      <button
        type="button"
        onClick={() => onOpenFullscreen(scene.scene)}
        title="Click to open fullscreen (raw vs current)"
        aria-label={`Open ${scene.scene} fullscreen`}
        className="relative aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-400"
      >
        {imgErrored ? (
          <div className="text-slate-400 text-xs flex flex-col items-center gap-1 p-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Failed to load</span>
          </div>
        ) : (
          <img
            // Remount on every version bump so the browser reloads the
            // freshly written crop even when the URL string is unchanged.
            key={`${scene.scene}:${version}`}
            src={displaySrc(scene.url, version)}
            alt={scene.scene}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setImgErrored(true)}
          />
        )}

        {/* Per-image busy overlay: dims the crop + spinner while this
            scene's action is in flight. Other cards stay interactive. */}
        {busy && <BusyOverlay />}

        <span className="absolute top-2 left-2">
          <StatusBadge approved={approved} />
        </span>
      </button>

      <div className="px-3 py-2 flex flex-col gap-2">
        <span className="text-sm font-medium text-slate-700 capitalize truncate">
          {scene.scene.replace(/_/g, ' ')}
        </span>

        <ActionBar
          scene={scene}
          busy={busy}
          onApprove={onApprove}
          onRegenerate={onRegenerate}
          onPrompt={onPrompt}
          onUndo={onUndo}
          onRedo={onRedo}
        />

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 break-words">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

interface SceneFullscreenProps {
  scene: ReviewScene;
  version: number;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onApprove: (scene: string) => void;
  onRegenerate: (scene: string) => void;
  onPrompt: (scene: string) => void;
  onUndo: (scene: string) => void;
  onRedo: (scene: string) => void;
}

// Shared wheel-zoom limits + the zoom/pan transform shape. Zoom is
// anchored at the panel centre (`transform-origin: center`) so a single
// shared transform maps to the identical region in both equally-sized
// panels — that's what keeps the Raw/Current comparison in true lockstep.
const MIN_SCALE = 1;
const MAX_SCALE = 6;

interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY_TRANSFORM: Transform = { scale: 1, tx: 0, ty: 0 };

interface ZoomPanelProps {
  /** Image URL, or null when the scene has no image of this kind. */
  src: string | null;
  /** Forces a fresh <img> mount (used to refetch the Current crop). */
  imgKey?: string;
  alt: string;
  errored: boolean;
  onError: () => void;
  /** Shown when there's no `src` (e.g. "No raw crop"). */
  missingLabel: string;
  /** Shared zoom/pan transform — the SAME object drives both panels. */
  transform: Transform;
  setTransform: React.Dispatch<React.SetStateAction<Transform>>;
  /** True while EITHER panel is being dragged (kills the transition so
   *  both panels track the pointer in perfect lockstep). */
  panning: boolean;
  onPanStart: () => void;
  onPanEnd: () => void;
  /** Optional overlay (e.g. the busy spinner) painted over the image. */
  children?: React.ReactNode;
}

/**
 * One image panel with synced wheel-zoom + drag-pan. The zoom/pan state
 * lives in the parent {@link SceneFullscreen} and is shared between the
 * Raw and Current panels, so scrolling OR dragging over either moves BOTH
 * by the same factor — the side-by-side comparison His Majesty asked for.
 * Wheel events use a non-passive listener so ``preventDefault`` stops the
 * page from scrolling. Double-click resets to 1×.
 */
function ZoomPanel({
  src,
  imgKey,
  alt,
  errored,
  onError,
  missingLabel,
  transform,
  setTransform,
  panning,
  onPanStart,
  onPanEnd,
  children,
}: ZoomPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseTx: number;
    baseTy: number;
    pointerId: number;
  } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setTransform((prev) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        // Snapping back to 1× recentres so the next zoom starts clean.
        if (nextScale <= MIN_SCALE) return IDENTITY_TRANSFORM;
        return { ...prev, scale: nextScale };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setTransform]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (transform.scale <= MIN_SCALE) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: transform.tx,
      baseTy: transform.ty,
      pointerId: e.pointerId,
    };
    onPanStart();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    e.preventDefault();
    setTransform((prev) => ({
      ...prev,
      tx: d.baseTx + (e.clientX - d.startX),
      ty: d.baseTy + (e.clientY - d.startY),
    }));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (d && e.pointerId === d.pointerId) {
      dragRef.current = null;
      onPanEnd();
    }
  };

  const isZoomed = transform.scale > MIN_SCALE;
  const cursor = isZoomed ? (panning ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in';
  const placeholderLabel = errored ? 'Failed to load' : missingLabel;

  return (
    <div
      ref={containerRef}
      onDoubleClick={() => setTransform(IDENTITY_TRANSFORM)}
      className="relative flex-1 min-h-0 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center"
    >
      {src && !errored ? (
        <img
          key={imgKey}
          src={src}
          alt={alt}
          draggable={false}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onError={onError}
          className={`max-w-full max-h-full object-contain select-none ${cursor}`}
          style={{
            transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
            transformOrigin: 'center center',
            transition: panning ? 'none' : 'transform 80ms ease-out',
            willChange: 'transform',
          }}
        />
      ) : (
        <div className="text-slate-400 text-xs flex flex-col items-center gap-1 p-4">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>{placeholderLabel}</span>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Fullscreen lightbox for one scene: the immutable "Raw crop" on the
 * left and the live "Current" version on the right, each blown up to use
 * nearly the whole viewport. The action bar under the Current panel is
 * the exact same {@link ActionBar} wired to the page-level handlers +
 * per-scene busy state, so an action started here disables the buttons
 * everywhere and shows the spinner over the Current image (and the
 * underlying card updates too). A single shared zoom/pan transform drives
 * BOTH panels (see {@link ZoomPanel}) for a locked side-by-side compare.
 * Closes on backdrop click or Esc. Mirrors `ImagesModal`'s Lightbox
 * z-index conventions (z-[60]; the PromptDialog at z-[70] stacks above
 * when the reviewer opens Prompt from here).
 */
function SceneFullscreen({
  scene,
  version,
  busy,
  error,
  onClose,
  onApprove,
  onRegenerate,
  onPrompt,
  onUndo,
  onRedo,
}: SceneFullscreenProps) {
  const [rawErrored, setRawErrored] = useState(false);
  const [curErrored, setCurErrored] = useState(false);
  // Zoom/pan shared across both panels, plus a "panning" flag so the
  // non-dragged panel drops its transition and tracks the pointer 1:1.
  const [transform, setTransform] = useState<Transform>(IDENTITY_TRANSFORM);
  const [panning, setPanning] = useState(false);
  const approved = scene.status === 'approved';
  const isZoomed = transform.scale > MIN_SCALE;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Clear a stale "failed to load" once the underlying source changes
  // (a fresh version after an action, or a different raw URL).
  useEffect(() => setCurErrored(false), [scene.url, version]);
  useEffect(() => setRawErrored(false), [scene.rawUrl]);
  // Reset zoom/pan to default whenever the overlay opens or switches to
  // another scene (His Majesty's requirement).
  useEffect(() => setTransform(IDENTITY_TRANSFORM), [scene.scene]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-3">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full h-full max-w-[98vw] max-h-[96vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Compact header: scene name · reset-zoom (when zoomed) · close. */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 sm:px-4 py-2 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 capitalize truncate">
            {scene.scene.replace(/_/g, ' ')}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            {isZoomed && (
              <button
                type="button"
                onClick={() => setTransform(IDENTITY_TRANSFORM)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
                title="Reset zoom (double-click image)"
                aria-label="Reset zoom"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9l6 6m0-6l-6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{Math.round(transform.scale * 100)}%</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close (Esc)"
              title="Close (Esc)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Image grid — two equal-size panels filling the viewport. */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-2 p-2">
          {/* Raw crop panel (immutable apollo crop). */}
          <div className="flex flex-col gap-1.5 min-h-0">
            <div className="flex items-center justify-between gap-2 h-7 shrink-0">
              <span className="text-sm font-medium text-slate-700">Raw crop</span>
            </div>
            <ZoomPanel
              src={scene.rawUrl}
              alt={`${scene.scene} raw crop`}
              errored={rawErrored}
              onError={() => setRawErrored(true)}
              missingLabel="No raw crop"
              transform={transform}
              setTransform={setTransform}
              panning={panning}
              onPanStart={() => setPanning(true)}
              onPanEnd={() => setPanning(false)}
            />
          </div>

          {/* Current version panel. */}
          <div className="flex flex-col gap-1.5 min-h-0">
            <div className="flex items-center justify-between gap-2 h-7 shrink-0">
              <span className="text-sm font-medium text-slate-700">Current</span>
              <StatusBadge approved={approved} />
            </div>
            <ZoomPanel
              src={scene.url ? displaySrc(scene.url, version) : null}
              imgKey={`${scene.scene}:${version}`}
              alt={`${scene.scene} current`}
              errored={curErrored}
              onError={() => setCurErrored(true)}
              missingLabel="Failed to load"
              transform={transform}
              setTransform={setTransform}
              panning={panning}
              onPanStart={() => setPanning(true)}
              onPanEnd={() => setPanning(false)}
            >
              {busy && <BusyOverlay />}
            </ZoomPanel>
          </div>
        </div>

        {/* Footer: zoom/pan hint under Raw · the shared action bar (+ any
            inline error) beneath the Current panel. */}
        <div className="grid grid-cols-2 gap-2 px-2 pb-2 pt-2 border-t border-slate-200 shrink-0">
          <div className="hidden sm:flex items-center text-xs text-slate-400">
            Scroll to zoom · drag to pan · double-click to reset
          </div>
          <div className="flex flex-col gap-2">
            <ActionBar
              scene={scene}
              busy={busy}
              onApprove={onApprove}
              onRegenerate={onRegenerate}
              onPrompt={onPrompt}
              onUndo={onUndo}
              onRedo={onRedo}
            />
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 break-words">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Per-scan manual review editor (dedicated route, reads `:jobId`).
 *
 * Shows the enhanced crops grid (no panorama). Each scene card carries a
 * status badge + an action bar (approve / regenerate / prompt / undo /
 * redo). Each action hits its endpoint, then patches just that scene from
 * the single-scene response (imperative no-react-query pattern). While an
 * action is in flight the whole card is disabled and its image shows a
 * spinner overlay; other cards stay interactive. The page-level Publish
 * button confirms the approved-scene count and fires the callback.
 */
export function ReviewScanPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [review, setReview] = useState<ScanReview | null>(null);
  const [scenes, setScenes] = useState<ReviewScene[]>([]);
  // Per-scene cache-bust token (0 = pristine; bumped to Date.now() on each
  // successful action so the crop image remounts + refetches).
  const [versions, setVersions] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pendingScenes, setPendingScenes] = useState<Set<string>>(new Set());
  const [sceneErrors, setSceneErrors] = useState<Record<string, string>>({});

  const [promptSceneName, setPromptSceneName] = useState<string | null>(null);
  // Scene currently shown in the fullscreen raw-vs-current overlay.
  const [fullscreenScene, setFullscreenScene] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchScanReview(jobId);
      setReview(data);
      setScenes(data.scenes);
      setVersions({});
    } catch (err) {
      log.error('Failed to load scan review:', err);
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const applySceneUpdate = useCallback((updated: ReviewScene) => {
    setScenes((prev) => prev.map((s) => (s.scene === updated.scene ? updated : s)));
    setVersions((prev) => ({ ...prev, [updated.scene]: Date.now() }));
  }, []);

  // Shared per-scene action runner: marks the scene busy (disables its
  // card + shows the image spinner), clears any prior error, applies the
  // single-scene response on success, and surfaces failures inline.
  const runSceneAction = useCallback(
    async (sceneName: string, fn: () => Promise<ReviewScene>) => {
      setPendingScenes((prev) => {
        const next = new Set(prev);
        next.add(sceneName);
        return next;
      });
      setSceneErrors((prev) => {
        const next = { ...prev };
        delete next[sceneName];
        return next;
      });
      try {
        const updated = await fn();
        applySceneUpdate(updated);
        return true;
      } catch (err) {
        log.error(`Action failed for scene ${sceneName}:`, err);
        setSceneErrors((prev) => ({
          ...prev,
          [sceneName]: err instanceof Error ? err.message : String(err),
        }));
        return false;
      } finally {
        setPendingScenes((prev) => {
          const next = new Set(prev);
          next.delete(sceneName);
          return next;
        });
      }
    },
    [applySceneUpdate],
  );

  const handleApprove = useCallback(
    (scene: string) => {
      if (!jobId) return;
      runSceneAction(scene, () => approveScene(jobId, scene));
    },
    [jobId, runSceneAction],
  );

  const handleRegenerate = useCallback(
    (scene: string) => {
      if (!jobId) return;
      runSceneAction(scene, () => regenerateScene(jobId, scene));
    },
    [jobId, runSceneAction],
  );

  const handleUndo = useCallback(
    (scene: string) => {
      if (!jobId) return;
      runSceneAction(scene, () => undoScene(jobId, scene));
    },
    [jobId, runSceneAction],
  );

  const handleRedo = useCallback(
    (scene: string) => {
      if (!jobId) return;
      runSceneAction(scene, () => redoScene(jobId, scene));
    },
    [jobId, runSceneAction],
  );

  const handlePromptSubmit = useCallback(
    (text: string) => {
      if (!jobId || !promptSceneName) return;
      const scene = promptSceneName;
      // Close the dialog IMMEDIATELY (His Majesty's bug fix): the prompt
      // text is already captured, so we dismiss the blocking modal first
      // and let the per-scene busy state take over — only that scene's
      // image + buttons are disabled (spinner over the Current image),
      // exactly like Regenerate. The long FAL call runs after the close.
      setPromptSceneName(null);
      runSceneAction(scene, () => promptScene(jobId, scene, text));
    },
    [jobId, promptSceneName, runSceneAction],
  );

  const approvedCount = useMemo(
    () => scenes.filter((s) => s.status === 'approved').length,
    [scenes],
  );

  const alreadyPublished = review?.status === 'published';

  const handlePublish = useCallback(async () => {
    if (!jobId) return;
    setPublishing(true);
    setPublishError(null);
    try {
      await publishScan(jobId);
      navigate('/interior/manual-verification');
    } catch (err) {
      log.error('Failed to publish scan:', err);
      setPublishError(err instanceof Error ? err.message : String(err));
      setPublishing(false);
      setPublishOpen(false);
    }
  }, [jobId, navigate]);

  const sortedScenes = useMemo(
    () => [...scenes].sort((a, b) => a.scene.localeCompare(b.scene)),
    [scenes],
  );

  // The live scene object backing the fullscreen overlay (kept in sync
  // with `scenes` so an action's single-scene response updates it). Null
  // when nothing is open or the scene vanished.
  const fullscreenSceneObj = useMemo(
    () =>
      fullscreenScene === null
        ? null
        : scenes.find((s) => s.scene === fullscreenScene) ?? null,
    [fullscreenScene, scenes],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate('/interior/manual-verification')}
            className="btn-secondary px-2.5 py-1.5 rounded-md text-sm flex items-center gap-1.5 shrink-0"
            title="Back to Manual Review"
            aria-label="Back to Manual Review"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900 truncate">Review scan</h1>
            {review && (
              <p className="text-xs text-slate-500 truncate">
                Scan <span className="font-mono">{review.scan_id}</span>
                {review.organization_id && (
                  <>
                    {' · '}
                    <span className="font-mono">{review.organization_id}</span>
                  </>
                )}
                {review.uvcamp_link && (
                  <>
                    {' · '}
                    <a
                      href={review.uvcamp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:underline"
                    >
                      UVCamp
                    </a>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {review && (
            <span className="text-sm text-slate-500">
              {approvedCount} approved / {scenes.length} scene{scenes.length !== 1 ? 's' : ''}
            </span>
          )}
          <button
            type="button"
            onClick={() => setPublishOpen(true)}
            disabled={loading || alreadyPublished || approvedCount === 0}
            className="btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              alreadyPublished
                ? 'Already published'
                : approvedCount === 0
                  ? 'Approve at least one scene to publish'
                  : 'Publish approved crops'
            }
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {alreadyPublished ? 'Published' : 'Publish'}
          </button>
        </div>
      </div>

      {publishError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {publishError}
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center gap-3 text-slate-500">
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading crops…
          </div>
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </div>
      ) : sortedScenes.length === 0 ? (
        <div className="p-12 text-center text-sm text-slate-500">
          No crops available for this scan.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedScenes.map((scene) => (
            <SceneCard
              key={scene.scene}
              scene={scene}
              version={versions[scene.scene] ?? 0}
              busy={pendingScenes.has(scene.scene)}
              error={sceneErrors[scene.scene] ?? null}
              onApprove={handleApprove}
              onRegenerate={handleRegenerate}
              onPrompt={setPromptSceneName}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onOpenFullscreen={setFullscreenScene}
            />
          ))}
        </div>
      )}

      {fullscreenSceneObj && (
        <SceneFullscreen
          scene={fullscreenSceneObj}
          version={versions[fullscreenSceneObj.scene] ?? 0}
          busy={pendingScenes.has(fullscreenSceneObj.scene)}
          error={sceneErrors[fullscreenSceneObj.scene] ?? null}
          onClose={() => setFullscreenScene(null)}
          onApprove={handleApprove}
          onRegenerate={handleRegenerate}
          onPrompt={setPromptSceneName}
          onUndo={handleUndo}
          onRedo={handleRedo}
        />
      )}

      <PromptDialog
        isOpen={promptSceneName !== null}
        scene={promptSceneName}
        onSubmit={handlePromptSubmit}
        onCancel={() => setPromptSceneName(null)}
        isLoading={promptSceneName !== null && pendingScenes.has(promptSceneName)}
      />

      <ConfirmDialog
        isOpen={publishOpen}
        title="Publish scan"
        message={`You are about to publish ${approvedCount} image${approvedCount !== 1 ? 's' : ''}, are you sure?`}
        confirmLabel="Publish"
        onConfirm={handlePublish}
        onCancel={() => setPublishOpen(false)}
        isLoading={publishing}
        variant="primary"
      />
    </div>
  );
}
